import {
  Component,
  EventEmitter,
  input,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { BuilderQuestion } from '../../../../classes/questionnaire-builder/builder-question';
import { QuestionType } from '../../../../api/models';
import { BuilderAnswerOption } from '../../../../classes/questionnaire-builder/builder-answer-option';
import { environment } from '../../../../environments/environment';
import { FileService } from '../../../../api/services/file.service';
import { AuthService } from '../../../../services/auth.service';
import { ToastService } from '../../../../services/toast.service';
import { BuilderAttachment } from '../../../../classes/questionnaire-builder/builder-attachment';
import { BuilderBranchCondition } from '../../../../classes/questionnaire-builder/builder-branch-condition';
import { BranchConditionOperator } from '../../../../enums/branch-condition-operator';
import { BranchConditionOperand } from '../../../../enums/branch-condition-operand';
import { BuilderBranchConditionComponent } from '../../../../classes/questionnaire-builder/builder-branch-condition-component';
import { Editor } from 'ngx-editor';
import { scaleHeightAndFadeAnimation } from '../../../../animations/scale-height-and-fade';
import { fadeInOutAnimation } from '../../../../animations/fade-in-out';
import { AlertSeverityLevel } from '../../../../enums/alert-severity-level';
import { KeyValue } from '@angular/common';

@Component({
  selector: 'app-edit-question',
  templateUrl: './edit-question.component.html',
  standalone: false,
  animations: [scaleHeightAndFadeAnimation, fadeInOutAnimation],
})
export class EditQuestionComponent implements OnDestroy {
  // The question being edited. If no question is passed as input, instead a new question will be created
  @Input({ required: false }) question!: BuilderQuestion;

  // All questions for the current questionnaire, used for conditionality
  @Input({ required: true }) availableBranchQuestions!: BuilderQuestion[];

  // Where the question is currently selected and should be expanded
  readonly selected = input<boolean>(false);

  // Event emitted when a question is deleted
  @Output() questionDeletedEvent = new EventEmitter<BuilderQuestion>();

  // Event emitted when a question is duplicated
  @Output() questionDuplicatedEvent = new EventEmitter<BuilderQuestion>();

  // Rich text editor configuration
  editor: Editor = new Editor();

  /*
    Whether the question editor is currently expanded
   */
  protected readonly QuestionType = QuestionType;
  protected readonly Object = Object;
  protected readonly environment = environment;

  constructor(
    private fileService: FileService,
    private authService: AuthService,
    private toastService: ToastService,
  ) {}

  ngOnDestroy(): void {
    this.editor.destroy();
  }

  /*
    Adds a new empty answer option to the question
   */
  addAnswerOption() {
    this.question.answerOptions.push(
      new BuilderAnswerOption('', 0, this.question.answerOptions.length),
    );
  }

  /*
    Deletes an answer option for this question
   */
  deleteAnswerOption(event: Event, answerOption: BuilderAnswerOption) {
    this.question.answerOptions = this.question.answerOptions.filter(
      (ao) => ao !== answerOption,
    );
  }

  /*
    Handler method for when the question type select input is changed
  */
  onQuestionTypeChange(selectedType: QuestionType) {
    const selectedTypeAllowsOptions =
      this.questionTypeAllowsOptions(selectedType);
    const currentTypeAllowsOptions = this.questionTypeAllowsOptions(
      this.question.type,
    );
    const willClearOptions =
      currentTypeAllowsOptions &&
      !selectedTypeAllowsOptions &&
      this.question.answerOptions.length > 0;

    if (
      willClearOptions &&
      !window.confirm(
        'Changing this question type will remove its answer options. Continue?',
      )
    ) {
      return;
    }

    this.question.type = selectedType;

    // Question types without options cannot retain option-specific data.
    if (!selectedTypeAllowsOptions) {
      this.question.answerOptions = [];
    }
  }

  private questionTypeAllowsOptions(questionType: QuestionType) {
    return (
      questionType === QuestionType.SingleChoice ||
      questionType === QuestionType.MultipleChoice ||
      questionType === QuestionType.Ranking ||
      questionType === QuestionType.RangeSelection
    );
  }

  addBranchCondition() {
    this.question.branchConditions.push(
      new BuilderBranchCondition(
        this.question.guid,
        this.availableBranchQuestions[0]?.guid,
        [
          new BuilderBranchConditionComponent(
            BranchConditionOperand.QuestionAnswer,
            BranchConditionOperator.Equals,
            '',
          ),
        ],
      ),
    );
  }

  /**
   * When file is inputted, we add it to the list of attachments, and upload it to the server
   */
  handleFileInput(event: Event) {
    const file = (event.target as HTMLInputElement).files![0];

    // Read file input to blob
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onload = () => {
      const blob = new Blob([reader.result as BlobPart], { type: file.type });

      // Upload file to server
      this.fileService
        .uploadFile$Response(
          {
            body: {
              file: blob,
              fileName: file.name,
              accountGuid: this.authService.getActiveAccountGuid()!,
            },
          },
          this.authService.getAuthorisationHeaders().delete('Content-Type'),
        )
        .subscribe({
          next: (res) => {
            const fileGuid = res.body;
            this.toastService.success('File Uploaded');
            this.question.attachments.push(
              new BuilderAttachment(file.name, fileGuid.replaceAll('"', '')),
            );
          },
          error: () => {
            this.toastService.error('File Could Not Be Uploaded');
          },
        });
    };
  }

  shouldShowConditionalitySection() {
    return (
      this.question.type === QuestionType.SingleChoice ||
      this.question.type === QuestionType.MultipleChoice ||
      this.question.type === QuestionType.RangeSelection
    );
  }

  deleteBranchCondition(branchCondition: BuilderBranchCondition) {
    this.question.branchConditions = this.question.branchConditions.filter(
      (b) => b !== branchCondition,
    );
  }

  protected readonly AlertSeverityLevel = AlertSeverityLevel;
  // eslint-disable-next-line
  protected unsorted(a: KeyValue<any, any>, b: KeyValue<any, any>): number {
    return 0;
  }
}
